import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, PaginatedTable, PaginatedTableColumn, Section } from '@common/components';
import { formatDate } from '@common/utils';
import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';
import { GENETIC_COUNSELING_TRANSLATION } from '@modules/genetic-counseling/constants';
import { useReferralsTable } from '@modules/genetic-counseling/hooks';
import { CompletedReferral, ReferralListItem, ReferralStatuses } from '@modules/genetic-counseling/types';
import BasicFilter from '../shared/BasicFilter/BasicFilter';

const Completed: FC = () => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();

  const {
    dataTable,
    commonColumns,
    pageSize,
    handleChangeSorting,
    handleChangePage,
    handleFilterChange,
    handleRowClick,
  } = useReferralsTable<CompletedReferral>(ReferralStatuses.COMPLETED);

  const columns: Array<PaginatedTableColumn<ReferralListItem> | PaginatedTableColumn<CompletedReferral>> = [
    {
      key: 'dateTime',
      label: t('table.completion_date'),
      render: (_column: PaginatedTableColumn<CompletedReferral>, item: CompletedReferral) => {
        return item.completionDateTime ? formatDate(item.completionDateTime, 'MM/dd/yyyy') : '--';
      },
      sort: true,
      width: '16%',
    },
    ...(isFeatureEnabled(FeatureFlags.NO_SHOW_REVAMP)
      ? [
          {
            key: 'status',
            label: t('table.status'),
            render: (_column: PaginatedTableColumn<CompletedReferral>, item: CompletedReferral) => {
              const statusLabels: Record<string, string> = {
                [ReferralStatuses.DELIVERED]: t('table.status_completed'),
                [ReferralStatuses.NO_SHOW]: t('table.status_no_show'),
              };
              return statusLabels[item.status] ?? '--';
            },
            width: '10%',
          },
        ]
      : []),
    ...commonColumns,
  ];

  return (
    <Section title={t('completed.section_title')}>
      <Box>
        <BasicFilter onFilterChange={handleFilterChange} />
        <PaginatedTable
          columns={columns}
          data={dataTable}
          isPaginated={false}
          onRowClick={handleRowClick}
          onSortingChange={handleChangeSorting}
          onPageChange={handleChangePage}
          pageSize={pageSize}
          rowClickable
        />
      </Box>
    </Section>
  );
};

export default Completed;
